import { AuthorizationParams } from '@auth0/auth0-vue'
import { nullUUID } from './UUIDHelper'

export const INSTANCE_ID_KEY = 'instanceId'
export const CLIENT_ID_KEY = 'clientId'
export const RETURN_PATH_KEY = 'return_path'

/**
 * Validates a return_path value from an external URL.
 * Returns the path unchanged if valid, null if it should be rejected.
 * Rejects anything that could form a protocol-relative or absolute URL.
 */
export function validateReturnPath(path: string | null): string | null {
  if (!path) return null
  if (!path.startsWith('/')) return null
  if (path.includes('//')) return null
  if (path.includes(':')) return null
  if (path.includes('\\')) return null
  return path
}

// Routes that run their own auth flow - returning to them after a login would replay that flow.
const AUTH_FLOW_PATHS = ['/silent-auth', '/switch-instances', '/logout', '/unauthorized']

/**
 * The route to return to after an interactive login, taken from the (hash-routed) location so it is
 * right even before the router's first navigation resolves. Undefined for home, auth-flow routes and
 * any path validateReturnPath rejects - the login then falls back to auth0-vue's default of '/'.
 * Only the path is validated: the query is kept as-is (values like `12:00` are legitimate), and it
 * can't make the target external since the path must already start with a single '/'.
 */
export function getLoginReturnTarget(hash: string): string | undefined {
  const route = hash.replace(/^#/, '')
  const queryStart = route.indexOf('?')
  const path = validateReturnPath(queryStart === -1 ? route : route.slice(0, queryStart))
  if (!path || path === '/') return undefined
  if (AUTH_FLOW_PATHS.some(authPath => path === authPath || path.startsWith(`${authPath}/`))) return undefined
  return queryStart === -1 ? path : `${path}${route.slice(queryStart)}`
}

export function isSwitchInstanceSignal(instanceId: string | null): boolean {
  return instanceId !== null && (instanceId.trim() === '' || instanceId.toLowerCase() === nullUUID)
}

export function createAuthorizationParameters(params: AuthorizationParams): AuthorizationParams {
  const instanceId = localStorage.getItem(INSTANCE_ID_KEY)
  const clientId = localStorage.getItem(CLIENT_ID_KEY)
  if (instanceId && !isSwitchInstanceSignal(instanceId)) {
    params.instance_id = instanceId
  }
  if (clientId) {
    params.skynamo_client_id = clientId
  }
  return params
}
